import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import type { WorkOrderExternalSnapshot } from '@myapp/contract'

const POPOVER_WIDTH = 320
const POPOVER_APPROX_HEIGHT = 420
const MOBILE_BREAKPOINT = 540

interface Props {
  anchorRect: DOMRect
  external: WorkOrderExternalSnapshot
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
}

interface Position {
  top: number
  left: number
  width: number
}

function computePosition(anchor: DOMRect): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (vw < MOBILE_BREAKPOINT) {
    const margin = 12
    const width = Math.min(POPOVER_WIDTH, vw - margin * 2)
    const left = Math.round((vw - width) / 2)

    let top = anchor.bottom + 8
    if (top + POPOVER_APPROX_HEIGHT > vh - margin) {
      top = anchor.top - POPOVER_APPROX_HEIGHT - 8
    }
    top = Math.max(margin, Math.min(top, vh - POPOVER_APPROX_HEIGHT - margin))

    return { top, left, width }
  }

  let left = anchor.right + 8
  if (left + POPOVER_WIDTH > vw - 8) {
    left = anchor.left - POPOVER_WIDTH - 8
  }
  left = Math.max(8, Math.min(left, vw - POPOVER_WIDTH - 8))

  let top = anchor.top
  if (top + POPOVER_APPROX_HEIGHT > vh - 8) {
    top = vh - POPOVER_APPROX_HEIGHT - 8
  }
  top = Math.max(8, top)

  return { top, left, width: POPOVER_WIDTH }
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('de-DE')
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-100 pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{title}</div>
      {children}
    </div>
  )
}

function Row({
  label,
  value,
  children,
}: {
  label: string
  value?: string | null
  children?: React.ReactNode
}) {
  if (!children && !value) return null
  return (
    <div className="flex text-sm text-gray-800 gap-2">
      <span className="text-gray-500 shrink-0 w-24">{label}</span>
      <span className="min-w-0 break-words select-text">{children ?? value}</span>
    </div>
  )
}

export function AppointmentInfoPopover({ anchorRect, external, onMouseEnter, onMouseLeave }: Props) {
  const [pos, setPos] = useState<Position>(() => computePosition(anchorRect))

  useEffect(() => {
    setPos(computePosition(anchorRect))
  }, [anchorRect])

  const leistung = external.leistungText
    ? external.infoText
      ? `${external.leistungText} (${external.infoText})`
      : external.leistungText
    : null

  const veLine = external.ve != null
    ? external.veAdresse ? `${external.ve} · ${external.veAdresse}` : String(external.ve)
    : external.veAdresse ?? null

  const wohnung = external.wohnung != null ? String(external.wohnung) : null

  const hasAdresse = !!(veLine || external.gebaeude || external.hauseingang || wohnung)
  const hasMieter = !!(external.mieter?.name || external.mieter?.telefon || external.mieter?.email)
  const hasDates = !!(external.auftragsdatum || external.ausfuehrungBis || external.fertigmeldungsdat)

  const telHref = external.mieter?.telefon ? `tel:${external.mieter.telefon.replace(/\s+/g, '')}` : null
  const mailHref = external.mieter?.email ? `mailto:${external.mieter.email}` : null

  const content = (
    <div
      className="fixed bg-white rounded-xl shadow-xl border border-gray-200 z-[9999] overflow-hidden"
      style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: POPOVER_APPROX_HEIGHT }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-4 py-3 overflow-y-auto" style={{ maxHeight: POPOVER_APPROX_HEIGHT }}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-800">
            Nr. {external.auftragJahr}/{external.auftragNr}
          </span>
          {external.isWohnungsmodernisierung && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#124093] text-white uppercase tracking-wide">
              Modernisierung
            </span>
          )}
        </div>

        {(external.gewerkeText || external.handwerkerName) && (
          <Section title="Gewerk">
            <Row label="Gewerk" value={external.gewerkeText} />
            <Row label="Handwerker" value={external.handwerkerName} />
          </Section>
        )}

        {leistung && (
          <Section title="Leistung">
            <div className="text-sm text-gray-800 whitespace-pre-wrap break-words select-text cursor-text">{leistung}</div>
          </Section>
        )}

        {external.individuellerText && (
          <Section title="Schadensbeschreibung">
            <div
              className="text-sm text-gray-800 whitespace-pre-wrap break-words overflow-y-auto select-text cursor-text"
              style={{ maxHeight: 140 }}
            >
              {external.individuellerText}
            </div>
          </Section>
        )}

        {hasAdresse && (
          <Section title="Adresse">
            <Row label="Wirtschaftseinheit" value={veLine} />
            <Row label="Gebäude" value={external.gebaeude} />
            <Row label="Hauseingang" value={external.hauseingang} />
            <Row label="Wohnung" value={wohnung} />
          </Section>
        )}

        {hasMieter && (
          <Section title="Mieter">
            <Row label="Name" value={external.mieter.name} />
            {external.mieter.telefon && telHref && (
              <Row label="Telefon">
                <a href={telHref} className="text-blue-700 hover:underline">
                  {external.mieter.telefon}
                </a>
              </Row>
            )}
            {external.mieter.email && mailHref && (
              <Row label="E-Mail">
                <a href={mailHref} className="text-blue-700 hover:underline break-all">
                  {external.mieter.email}
                </a>
              </Row>
            )}
          </Section>
        )}

        {hasDates && (
          <Section title="Termine">
            <Row label="Auftragsdatum" value={formatDate(external.auftragsdatum)} />
            <Row label="Ausführung bis" value={formatDate(external.ausfuehrungBis)} />
            <Row label="Fertigmeldung" value={formatDate(external.fertigmeldungsdat)} />
          </Section>
        )}

        {external.erfasserName && (
          <div className="border-t border-gray-100 pt-2 mt-2 text-[11px] text-gray-400">
            Erfasser: {external.erfasserName}
          </div>
        )}
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}
